import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";

import { Breadcrumbs } from "./components/Breadcrumbs.jsx";
import { ErrorAlert } from "./components/ErrorAlert.jsx";
import { useWithdrawalWizardContext } from "./useWithdrawalWizard.js";

const inputClass =
  "wp:block wp:w-full wp:appearance-none wp:rounded-md wp:border wp:border-solid wp:border-gray-300 wp:[background-color:var(--wf-input-bg,#ffffff)] wp:px-4 wp:py-3 wp:text-lg wp:[color:var(--wf-text-color,#111827)] wp:shadow-sm wp:focus:border-gray-900 wp:focus:outline-none wp:focus:ring-1 wp:focus:ring-gray-900";

export function CustomerStep({
  additionalContent = {},
  orderReferenceExample,
  designMode,
  enableWithdrawalStatus,
  statusPath,
}) {
  const { t } = useTranslation();
  const { register, handleSubmit } = useFormContext();
  const { error, loading, runLookup } = useWithdrawalWizardContext();

  return (
    <div className="wp:mx-auto wp:max-w-xl">
      <Breadcrumbs current={1} />
      <h1 className="wp:mb-4 wp:text-3xl wp:font-normal wp:tracking-tight wp:[color:var(--wf-text-color,#111827)] wp:md:text-4xl">
        {t("proxy.withdrawals.new.title")}
      </h1>
      <p className="wp:mb-8 wp:text-lg wp:text-gray-600">{t("proxy.withdrawals.new.description")}</p>
      <AdditionalContentBlock content={additionalContent.above_form} />
      <ErrorAlert error={error} />

      <form
        onSubmit={handleSubmit(runLookup)}
      >
        <div className="wp:mb-6 wp:grid wp:grid-cols-2 wp:gap-4">
          <Field
            id="widerruf-first-name"
            label={t("proxy.withdrawals.common.fields.first_name")}
            registration={register("customer_first_name", { required: true })}
            autoComplete="given-name"
          />
          <Field
            id="widerruf-last-name"
            label={t("proxy.withdrawals.common.fields.last_name")}
            registration={register("customer_last_name", { required: true })}
            autoComplete="family-name"
          />
        </div>
        <Field
          id="widerruf-email"
          label={t("proxy.withdrawals.common.fields.email")}
          help={t("proxy.withdrawals.common.fields.email_help")}
          registration={register("customer_email", { required: true })}
          type="email"
          autoComplete="email"
        />
        <Field
          id="widerruf-order-reference"
          label={t("proxy.withdrawals.new.order_reference_label", {
            example: orderReferenceExample,
          })}
          help={t("proxy.withdrawals.new.order_reference_help")}
          registration={register("order_reference", { required: true })}
          autoComplete="off"
        />
        <AdditionalContentBlock content={additionalContent.below_form} />

        <button
          type="submit"
          disabled={loading || designMode}
          className="wp:w-full wp:appearance-none wp:rounded-md wp:border wp:border-solid wp:[border-color:var(--wf-button-border,#111827)] wp:[background-color:var(--wf-button-bg,#111827)] wp:px-6 wp:py-4 wp:text-lg wp:font-medium wp:[color:var(--wf-button-text,#ffffff)] wp:shadow-sm wp:transition-opacity wp:enabled:cursor-pointer wp:hover:opacity-90 wp:focus:outline-none wp:focus:ring-2 wp:focus:ring-gray-900 wp:focus:ring-offset-2 wp:disabled:cursor-not-allowed wp:disabled:opacity-50"
        >
          {t("proxy.withdrawals.common.actions.next")}
        </button>
      </form>

      {enableWithdrawalStatus ? (
        <p className="wp:mt-8 wp:text-center wp:text-base wp:text-gray-600">
          {t("proxy.withdrawals.new.status_prompt")}{" "}
          <a className="wp:font-medium wp:[color:var(--wf-text-color,#111827)] wp:underline" href={statusPath}>
            {t("proxy.withdrawals.common.actions.check_status")}
          </a>
        </p>
      ) : null}
    </div>
  );
}

function AdditionalContentBlock({ content }) {
  const text = String(content || "").trim();
  if (!text) return null;

  return (
    <div className="wp:mb-8 wp:whitespace-pre-wrap wp:text-base wp:leading-6 wp:text-gray-700">
      {text}
    </div>
  );
}

function Field({ id, label, help, registration, type = "text", autoComplete }) {
  return (
    <div className="wp:mb-6">
      <label htmlFor={id} className="wp:mb-2 wp:block wp:text-base wp:font-medium wp:[color:var(--wf-text-color,#111827)]">
        {label} <span className="wp:text-red-600">*</span>
      </label>
      {help ? <span className="wp:mb-3 wp:block wp:text-sm wp:text-gray-500">{help}</span> : null}
      <input
        id={id}
        required
        type={type}
        autoComplete={autoComplete}
        {...registration}
        className={inputClass}
      />
    </div>
  );
}
